import { useEffect, useRef, useState } from 'react'

import { api } from '@/api/client'
import type { PurchasePartnerHit } from '@/types/models'
import { inputCls } from '@/components/Field'
import { cn } from '@/lib/utils'

interface Props {
  label: string                       // 현재 선택 라벨(회사 · 거래처)
  onChange: (p: PurchasePartnerHit) => void
  placeholder?: string
  withGroups?: boolean                // 상위거래처도 함께 검색(is_group)
}

/** 전사(회사무관) 거래처 검색 콤보 — /api/purchase/partners?q= 사용.
 *  회사관리 구매장/라벨 매핑 폼 전용(전 회사 거래처를 한 번에 검색). */
export function PurchasePartnerCombo({ label, onChange, placeholder = '거래처 검색...', withGroups = false }: Props) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [hits, setHits] = useState<PurchasePartnerHit[]>([])
  const [truncated, setTruncated] = useState(false)
  const [loading, setLoading] = useState(false)
  const boxRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    const t = setTimeout(() => {
      api<{ rows: PurchasePartnerHit[]; truncated: boolean }>(`/api/purchase/partners?q=${encodeURIComponent(q)}${withGroups ? '&groups=1' : ''}`)
        .then((r) => { if (!cancelled) { setHits(r.rows); setTruncated(r.truncated) } })
        .catch(() => { if (!cancelled) { setHits([]); setTruncated(false) } })
        .finally(() => { if (!cancelled) setLoading(false) })
    }, 250)
    return () => { cancelled = true; clearTimeout(t) }
  }, [q, open, withGroups])

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  return (
    <div ref={boxRef} className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)}
              className={cn(inputCls, 'text-left', !label && 'text-slate-400')}>
        {label || placeholder}
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-72 overflow-auto">
          <div className="p-2 sticky top-0 bg-white border-b border-slate-100">
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="회사/거래처 검색"
                   className={inputCls + ' text-sm'}/>
          </div>
          {loading && <div className="px-3 py-2 text-xs text-slate-400">검색 중...</div>}
          {!loading && !hits.length && (
            <div className="px-3 py-2 text-xs text-slate-400">검색 결과 없음</div>
          )}
          {hits.map((h) => (
            <button key={`${h.is_group ? 'g' : 'p'}${h.id}`} type="button"
                    onClick={() => { onChange(h); setOpen(false); setQ('') }}
                    className="w-full text-left px-3 py-1.5 text-sm hover:bg-emerald-50">
              <span className="text-[11px] text-slate-400 mr-1.5">{h.company_name}</span>
              {h.is_group && (
                <span className="text-[10px] font-semibold text-violet-700 bg-violet-50 border border-violet-200 rounded px-1 mr-1">상위</span>
              )}
              <span className="font-mono text-xs text-slate-400 mr-1">{h.code}</span>
              {h.name}
            </button>
          ))}
          {truncated && (
            <div className="px-3 py-1.5 text-[11px] text-amber-600 bg-amber-50 border-t border-amber-100">
              결과가 많습니다 — 거래처명을 더 입력해 좁혀주세요.
            </div>
          )}
        </div>
      )}
    </div>
  )
}
